import React, { Dispatch, ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pagination, Space, Table } from 'antd';
import { Key, SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';
import { isEmpty } from 'lodash';

import {
  Contract,
  PricingRoutes,
  ContractStatusId,
  ContentItemBase,
} from 'pricing/types';
import { pricingContent } from 'pricing/utilities/localStorage/pricingContentStorage';
import ContractTermsTable from 'pricing/components/contractTerms/contractTermsTable/ContractTermsTable';
import { dateSort, defaultSort } from 'utilities/sorts/sorts';
import date from 'utilities/dates/date';
import {
  ContractListingAction,
  ContractListState,
} from 'pricing/pages/contractList/ContractList.state';
import { Nullable } from 'types';

interface Props {
  dispatch: Dispatch<ContractListingAction>;
  contractsState: ContractListState;
  onChange: (
    _pagination: TablePaginationConfig,
    _filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<Contract> | SorterResult<Contract>[],
  ) => void;
  onPageChange: (page: number, pageSize: number | undefined) => void;
}

function ContractListTable(props: Props): ReactElement {
  const {
    dispatch,
    onChange,
    onPageChange,
    contractsState: { loading, contracts, pagination, expandAllContracts, search },
  } = props;

  const [expandContractsKeys, setExpandContractsKeys] = useState<Key[]>([]);
  const [contractData, setContractData] = useState<Contract[]>();

  const openOrCloseAll = (type: boolean) => {
    let contractsId: (number | null | undefined)[] = [];
    if (contracts) contractsId = contracts.map((i) => i.id);
    type ? setExpandContractsKeys(contractsId as Key[]) : setExpandContractsKeys([]);
    dispatch({ type: 'EXPAND_ALL_CONTRACTS', payload: type });
  };

  useEffect(() => {
    openOrCloseAll(expandAllContracts);
  }, [expandAllContracts]);

  useEffect(() => {
    setContractData(contracts);
  }, [contracts]);

  return (
    <>
      <div className="expand-all-rows">
        <div className="expand-all-rows-btn">
          <div
            onClick={() => openOrCloseAll(!expandAllContracts)}
            className={`ant-table-row-expand-icon ${
              !expandAllContracts
                ? 'ant-table-row-expand-icon-collapsed'
                : 'ant-table-row-collapsed'
            }`}
          />
        </div>
      </div>

      <Table
        onChange={onChange}
        loading={loading}
        dataSource={contractData}
        pagination={false}
        scroll={{ x: true }}
        rowKey={(contractTerm) => contractTerm.id as number}
        columns={[
          {
            title: 'Contract ID',
            dataIndex: 'id',
            key: 'id',
            sorter: (a, b) => defaultSort(a?.id ?? '', b?.id ?? ''),
          },
          {
            title: 'Breeder ID',
            dataIndex: 'breederId',
            key: 'breeder_id',
            sorter: (a, b) => defaultSort(a?.breederId ?? '', b?.breederId ?? ''),
          },
          {
            title: 'Breeder Name',
            dataIndex: 'breederName',
            key: 'breeder_name',
            sorter: (a, b) => defaultSort(a?.companyName ?? '', b?.companyName ?? ''),
          },
          {
            title: 'Created By',
            dataIndex: 'createdBy',
            key: 'created_by',
            sorter: (a, b) => defaultSort(a?.createdBy ?? '', b?.createdBy ?? ''),
          },
          {
            title: 'Total Qty',
            dataIndex: 'totalQuantity',
            key: 'total_qty',
          },
          {
            title: 'Start Date',
            dataIndex: 'startDate',
            key: 'start_date',
            sorter: (a, b) => dateSort(a?.startDate, b?.startDate ?? ''),
            render: (startDate: string) => date.toUSLocale(startDate),
          },
          {
            title: 'End Date',
            dataIndex: 'endDate',
            key: 'end_date',
            sorter: (a, b) => dateSort(a?.endDate, b?.endDate ?? ''),
            render: (endDate: string) => date.toUSLocale(endDate),
          },
          {
            title: 'Condition',
            dataIndex: 'breederContractConditionId',
            key: 'breeder_contract_condition_id',
            sorter: (a, b) =>
              defaultSort(
                a?.breederContractConditionId ?? '',
                b?.breederContractConditionId ?? '',
              ),
            render: (breederContractConditionId: number) => {
              return pricingContent?.contractConditions?.filter(
                (i: ContentItemBase) => i.id === breederContractConditionId,
              )?.[0].name;
            },
          },
          {
            title: 'Actions',
            key: 'action',
            render: (contractTerm) => {
              const contractId = contractTerm.id;
              return (
                <Space size={24}>
                  <Link to={`${PricingRoutes.ContractView}/${contractId}`}>View</Link>
                  {search.breeder_contract_status_id !== ContractStatusId.Discharged && (
                    <Link to={`${PricingRoutes.ContractEdit}/${contractId}`}>Edit</Link>
                  )}
                </Space>
              );
            },
          },
        ]}
        expandable={{
          onExpand: (open, contract) => {
            const newExp: Nullable<string | number>[] = open
              ? [...expandContractsKeys, contract.id]
              : expandContractsKeys.filter((i) => i !== contract.id);
            setExpandContractsKeys(newExp as Key[]);
          },
          expandedRowKeys: expandContractsKeys,
          expandedRowRender: (contractTerm) => {
            const contractTerms = contractTerm?.breederContractTerms?.sort(
              (a, b) => a.id - b.id,
            );
            return <ContractTermsTable contractTerms={contractTerms} />;
          },
          rowExpandable: (contractTerm) => !isEmpty(contractTerm?.breederContractTerms),
        }}
      />
      {!isEmpty(contracts) && (
        <Space className="pagination-contract-list">
          <Pagination
            current={pagination.currentPage}
            pageSize={pagination.pageSize}
            pageSizeOptions={['10', '20', '25', '50', '100']}
            showSizeChanger
            total={pagination.totalContracts}
            onChange={onPageChange}
          />
        </Space>
      )}
    </>
  );
}

export default ContractListTable;
